import { Button, ButtonVariant } from "components/Form/Button";
import { InputPassword } from "components/Form/InputPassword";
import { Layout } from "components/Layout";
import { Field } from "components/Form/Field";
import { memo } from "react";
import { ReactComponent as Ai } from "assets/ai.svg";
import { UseFormRegister, FieldErrors } from "react-hook-form";
import { PasswordSetSuccessModal } from "./PasswordSetSuccessModal";

type PasswordFormValue = {
  password: string;
  confirmPassword: string;
};

interface PasswordStepProps {
  processing: boolean;
  isPasswordValid: boolean;
  passwordErrors: FieldErrors<PasswordFormValue>;
  showPasswordSuccessModal: boolean;
  registerPassword: UseFormRegister<PasswordFormValue>;
  onPasswordSubmit: (e: React.FormEvent) => void;
  onBackClick: () => void;
  originalEmail: string;
  verifiedEmail: string;
}

export const PasswordStep = memo(function PasswordStep({
  processing,
  isPasswordValid,
  passwordErrors,
  showPasswordSuccessModal,
  registerPassword,
  onPasswordSubmit,
  onBackClick,
  originalEmail,
  verifiedEmail,
}: PasswordStepProps) {
  return (
    <>
      <form className="contents" onSubmit={onPasswordSubmit}>
        <Layout
          className="h-full text-center"
          toolbar={
            <>
              <Button disabled={!isPasswordValid || processing} type="submit">
                {processing ? "Setting password..." : "Set Password"}
              </Button>

              <Button
                variant={ButtonVariant.FLAT}
                type="button"
                onClick={onBackClick}
              >
                Back to Login
              </Button>
            </>
          }
        >
          <div className="grid h-full space-y-12">
            <div className="grid self-start justify-items-center">
              <Ai />

              <h1 className="text-24 leading-1.2 font-semibold mt-4">
                Set Your Password
              </h1>

              <p className="text-858494 mt-3">
                Create a secure password for your account
              </p>

              {originalEmail && verifiedEmail && (
                <div className="mt-6 space-y-2">
                  <p className="text-14 text-858494">
                    Account Email:{" "}
                    <span className="text-gray-900 font-medium">
                      {originalEmail}
                    </span>
                  </p>
                  {originalEmail !== verifiedEmail && (
                    <p className="text-14 text-858494">
                      Contact Email:{" "}
                      <span className="text-gray-900 font-medium">
                        {verifiedEmail}
                      </span>
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="text-left space-y-6">
              <Field
                label="Password"
                description={passwordErrors.password?.message}
                descriptionClassName={
                  passwordErrors.password?.message ? "text-fd380f" : undefined
                }
                as="label"
              >
                <InputPassword
                  id="password"
                  autoComplete="new-password"
                  autoFocus
                  disabled={processing}
                  placeholder="Enter your password"
                  {...registerPassword("password", {
                    required: true,
                    minLength: 8,
                  })}
                />
              </Field>

              <Field
                label="Confirm Password"
                description={passwordErrors.confirmPassword?.message}
                descriptionClassName={
                  passwordErrors.confirmPassword?.message
                    ? "text-fd380f"
                    : undefined
                }
                as="label"
              >
                <InputPassword
                  id="confirmPassword"
                  autoComplete="new-password"
                  disabled={processing}
                  placeholder="Confirm your password"
                  {...registerPassword("confirmPassword", { required: true })}
                />
              </Field>
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <p className="text-14 font-medium text-gray-900 mb-2">
                  Password requirements:
                </p>
                <ul className="text-14 text-858494 space-y-1">
                  <li>• At least 8 characters</li>
                  <li>• One uppercase letter</li>
                  <li>• One lowercase letter</li>
                  <li>• One number</li>
                </ul>
              </div>
            </div>
          </div>
        </Layout>
      </form>

      <PasswordSetSuccessModal show={showPasswordSuccessModal} />
    </>
  );
});
